<template>
  <section :class="$style.content" data-test="contact-request-email-verification-panel">
    <HgLink
      v-if="!isInModal"
      tag="button"
      type="button"
      :class="$style.backButton"
      data-test="contact-request-email-verification-back"
      @click="emit('back')"
    >
      <HgIcon icon="ArrowLeft" size="small" theme="current" />
      {{ t('emailVerificationModal.back') }}
    </HgLink>

    <div :class="$style.illustrationContainer" aria-hidden="true">
      <img v-if="illustrationSrc" :class="$style.illustration" :src="illustrationSrc" alt="" />
    </div>

    <div :class="$style.copy">
      <h2 :class="$style.title">
        {{ t('emailVerificationCodePanel.title') }}
      </h2>

      <p :class="$style.description">{{ t('emailVerificationCodePanel.description') }}</p>
    </div>

    <div :class="$style.confirmationSentTo">
      <HgIcon icon="Email" theme="secondary" size="large" :class="$style.icon" />
      <div :class="$style.pushTextRight">
        <p>{{ t('emailVerificationCodePanel.confirmation.sent') }}</p>
        <strong>{{ email }}</strong>
      </div>
    </div>

    <VerificationCode
      :code="verificationCode"
      :label="t('emailVerificationCodePanel.codeLabel')"
      :codeLength="VERIFICATION_CODE_LENGTH"
      @update:code="handleCodeUpdate"
      @submit="handleConfirm"
    />

    <HgNotificationMessage
      v-if="confirmationFeedback"
      data-test="contact-request-email-verification-feedback"
      :title="t(confirmationFeedback.titleKey)"
      :theme="confirmationFeedback.theme"
    >
      {{ t(confirmationFeedback.messageKey) }}
      <p v-if="confirmationFeedback.messageKey2" :class="$style.feedbackMessage">
        {{ t(confirmationFeedback.messageKey2) }}
      </p>
    </HgNotificationMessage>

    <HgButton
      data-test="contact-request-email-verification-confirm"
      :disabled="!isCodeComplete || isConfirmInProgress"
      @click="handleConfirm"
    >
      <template v-if="isConfirmInProgress" #icon><LoadingSpinner /></template>
      {{ t('emailVerificationCodePanel.confirm') }}
    </HgButton>

    <ResendEmailVerificationButton :contactRequestId="props.contactRequestId" />
  </section>
</template>

<script setup lang="ts">
import { HgButton, HgIcon, HgLink, HgNotificationMessage } from '@smg-real-estate/domus';
import { computed, defineAsyncComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { internalAxios } from '@/helpers/http/internalAxiosClient';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { useContactFormStore } from '@/store/modules/contact-form';
import { ContactFormSubmitStatus } from '@/store/types/contact-form-state';
import { trackContactRequestEmailVerificationConfirmation } from '@/views/ContactRequestEmailVerificationPage/email-verification-tracking-events';

import ResendEmailVerificationButton from './ResendEmailVerificationButton.vue';
import VerificationCode from './VerificationCode.vue';

defineOptions({
  name: 'ContactRequestEmailVerificationCodePanel',
});

const VERIFICATION_CODE_LENGTH = 4;

const EMAIL_VERIFICATION_STATUS = {
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  EMAIL_ALREADY_VERIFIED: 'EMAIL_ALREADY_VERIFIED',
  EMAIL_VERIFICATION_FAILED: 'EMAIL_VERIFICATION_FAILED',
  EMAIL_VERIFICATION_EXPIRED: 'EMAIL_VERIFICATION_EXPIRED',
  EMAIL_VERIFICATION_THRESHOLD_REACHED: 'EMAIL_VERIFICATION_THRESHOLD_REACHED',
  NOT_FOUND: 'NOT_FOUND',
  UNCAUGHT_ERROR: 'UNCAUGHT_ERROR',
} as const;

const VERIFICATION_FAILURE_TITLE_KEY = 'emailVerificationFailure.title';

const KNOWN_FAILURE_STATUSES = new Set<string>([
  EMAIL_VERIFICATION_STATUS.EMAIL_VERIFICATION_FAILED,
  EMAIL_VERIFICATION_STATUS.EMAIL_VERIFICATION_EXPIRED,
  EMAIL_VERIFICATION_STATUS.EMAIL_VERIFICATION_THRESHOLD_REACHED,
]);

const getConfirmationErrorStatus = (error: unknown): string => {
  const status: string | undefined = (error as { response?: { data?: { status?: unknown } } })
    ?.response?.data?.status as string;

  return status && KNOWN_FAILURE_STATUSES.has(status)
    ? status
    : EMAIL_VERIFICATION_STATUS.UNCAUGHT_ERROR;
};

interface ConfirmationFeedback {
  theme: 'danger' | 'info' | 'warning';
  titleKey: string;
  messageKey: string;
  messageKey2?: string;
}

interface Props {
  email?: string;
  contactRequestId: string;
  isInModal?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  email: '',
  isInModal: false,
});

const emit = defineEmits<{
  back: [];
}>();

const { t } = useI18n();
const contactFormStore = useContactFormStore();
const LoadingSpinner = defineAsyncComponent(() => import('@/assets/loading_spinner.svg'));

const verificationCode = ref('');
const confirmationStatus = ref<string | null>(null);
const isConfirmInProgress = ref(false);
const illustrationSrc = ref<string>();

const confirmationFeedback = computed((): ConfirmationFeedback | null => {
  const status = confirmationStatus.value;
  if (!status) {
    return null;
  }

  if (status === EMAIL_VERIFICATION_STATUS.EMAIL_VERIFICATION_EXPIRED) {
    return {
      theme: 'danger',
      titleKey: VERIFICATION_FAILURE_TITLE_KEY,
      messageKey: 'emailVerificationCodePanel.expired.message',
    };
  }

  if (status === EMAIL_VERIFICATION_STATUS.EMAIL_VERIFICATION_THRESHOLD_REACHED) {
    return {
      theme: 'warning',
      titleKey: 'emailVerificationCodePanel.thresholdReached.title',
      messageKey: 'emailVerificationCodePanel.thresholdReached.message',
    };
  }

  if (status === EMAIL_VERIFICATION_STATUS.EMAIL_VERIFICATION_FAILED) {
    return {
      theme: 'danger',
      titleKey: VERIFICATION_FAILURE_TITLE_KEY,
      messageKey: 'emailVerificationCodePanel.failure.message',
    };
  }

  return {
    theme: 'danger',
    titleKey: VERIFICATION_FAILURE_TITLE_KEY,
    messageKey: 'emailVerificationCodePanel.uncaughtError.message',
  };
});

const isCodeComplete = computed(
  (): boolean => verificationCode.value.length === VERIFICATION_CODE_LENGTH,
);

const handleCodeUpdate = (value: string) => {
  verificationCode.value = value;
  confirmationStatus.value = null;
};

const applyConfirmationStatus = (status: string, contactRequestId?: string) => {
  if (status === EMAIL_VERIFICATION_STATUS.EMAIL_VERIFIED) {
    contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.SUCCESS);

    if (contactRequestId) {
      trackContactRequestEmailVerificationConfirmation(contactRequestId);
    }

    return;
  }

  if (status === EMAIL_VERIFICATION_STATUS.EMAIL_ALREADY_VERIFIED) {
    contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.SUCCESS);
  }
};

const handleConfirm = async () => {
  if (!isCodeComplete.value || isConfirmInProgress.value) return;

  isConfirmInProgress.value = true;
  confirmationStatus.value = null;

  try {
    const response = await internalAxios.post<{ status: string; contactRequestId?: string }>(
      `${import.meta.env.VUE_CONTACT_SERVICE_API_ENDPOINT}/contact-request/email-verification/confirm`,
      {
        contactRequestId: props.contactRequestId,
        code: verificationCode.value,
      },
    );

    applyConfirmationStatus(response.data.status, response.data.contactRequestId);
  } catch (error) {
    confirmationStatus.value = getConfirmationErrorStatus(error);
  } finally {
    isConfirmInProgress.value = false;
  }
};

const setIllustration = async () => {
  const importedIllustration = await import(
    isPlatformIs24()
      ? '@/assets/contact-request/email-verification-mail-delivery-is24.svg?url'
      : '@/assets/contact-request/email-verification-megaphone.svg?url'
  );

  illustrationSrc.value = importedIllustration.default;
};

setIllustration();
</script>

<style lang="scss" module>
@import '@/assets/variables';

$illustration-size: 92px;

.content {
  display: flex;
  flex-direction: column;
  gap: $space-75;
}

.backButton {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: $space-25;
  padding: 0;
  color: var(--base-color-link);
  font-size: var(--typo-body-3-font-size);
  font-weight: 700;
  line-height: var(--typo-body-3-line-height);
}

.illustrationContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: $illustration-size;
  height: $illustration-size;
  margin-top: $space-25;
}

.illustration {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-50;
}

.title {
  @include heading-3;

  margin: 0;
  text-align: center;
}

.description {
  @include body-3;

  max-width: 18rem;
  margin: 0;
  text-align: center;
}

.confirmationSentTo {
  @include body-3;

  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: $space-50;
  align-items: start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: $space-50 $space-75;
  background: $color-grey-30;
  border-radius: $border-radius;
  margin-bottom: $space-50;

  .icon {
    align-self: start;
  }

  .pushTextRight {
    display: flex;
    flex-direction: column;
    gap: $space-25;
    align-items: flex-start;
    min-width: 0;

    strong {
      @include body-3-bold;
    }

    p,
    strong {
      margin: 0;
      overflow-wrap: anywhere;
    }
  }
}

.feedbackMessage {
  margin: $space-25 0 0;
}
</style>
